import type { Fraction } from '@mimir/common/util/fractionType';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { PlayableArtifact, PlayableArtifactFlat, PlayableItemDto, VirtualClipInfo } from '@mimir/common/dto/playableItemDto';
import type { SegmentProvider, SegmentInfo } from '@mimir/common/types/segmentProvider';
import { CachedSegmentProvider } from '@mimir/common/cutter/cachedSegmentProvider';
import type { EncodedData } from '@mimir/common/mpegts/demuxerTypes';
import { addFractions, approximateFractionValue, compareFractions, divideFractions, floorFraction, formatFractionAsExpression, multiplyFractionBy, multiplyFractions } from '@mimir/common/util/integerMath';
import { frac } from '@mimir/common/util/integerMath';
import { ItemSegmentUrlCache } from '@mimir/client-common/util/itemUrlCache';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { Logger } from '@mimir/common/types/logger';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { decodeFmp4InitSegment, demuxFmp4Segment } from '@mimir/common/mp4/fmp4Demuxer';
import type { Mp4Info } from '@mimir/common/mp4/mp4Types';
import { fetchAndInspectMoov } from '@mimir/common/mp4/mp4Demuxer';
import { buildEncodedDataFromVirtualSegment, findVirtualSegmentIndex } from '@mimir/common/mp4/virtualSegments';
import { fetchByteArrays } from '@mimir/common/mp4/fetchByteArrays';
import type { ItemPollingOps } from '@mimir/client-common/polling/itemPollingOps';
import type { SegmentedMediaInfo } from '@mimir/common/dto/pollingDtos';
import { getAvailableQualitiesFromItem } from '@mimir/common/util/getAvailableQualitiesFromItem';
import type { PlaybackQuality, QualityOption } from '@mimir/common/types/playbackQuality';
import type { Clock } from '@mimir/common/types/clock';
import { resilientFetch } from '@mimir/common/io/resilientFetch';
import { isRetryableSegmentResponse } from '@mimir/common/util/isRetryableSegmentResponse';
import type { PlayableArtifactSelectionOptions } from '@mimir/common/util/selectPlayableArtifact';
import { getCutterPlayableItemGetOptions, selectCutterPlayableArtifact } from './cutterPlayableArtifact';
import { ItemNotPlayableYet } from '@mimir/common/types/knownErrors';
import { invariant } from '@mimir/common/util/invariant';
import { FRAC_ZERO } from '@mimir/common/util/fractionType';
import { createTamsSegmentSource } from './tamsSegmentSource';

export interface MimirSegmentProviderServices {
  apiActions: VideoApiActions;
  fetch: BasicFetch;
  logger: Logger;
  itemPollingOps: ItemPollingOps;
  clock: Clock;
  demuxTs: (bytes: Uint8Array, firstSegment: boolean) => Promise<EncodedData>;
}

export function validateAudioTrackLimit(item: PlayableItemDto, playableArtifact: PlayableArtifactFlat, mp4Info: Mp4Info | undefined): boolean {
  if (mp4Info === undefined) {
    return true;
  }
  if (playableArtifact.type === 'flatEditProxy' || playableArtifact.type === 'flatHighRes') {
    return true;
  } else if (playableArtifact.type === 'flatWebProxy') {
    return item.proxyAudioTrackUrls.length === 2 && item.webProxyAudioDownMixInfo === null;
  }
  return true;
}

export async function createMimirSegmentProvider(
  services: MimirSegmentProviderServices,
  itemId: string,
  artifactSelectionOptions: PlayableArtifactSelectionOptions,
  ignoreVideo: boolean,
  ignoreAudio: boolean,
): Promise<SegmentProvider> {
  const key = createKey(itemId, artifactSelectionOptions);
  return await CachedSegmentProvider.create(
    services.logger,
    key,
    async () => createMimirSegmentProviderInternal(services, itemId, artifactSelectionOptions),
    ignoreVideo,
    ignoreAudio,
  );
}


async function createMimirSegmentProviderInternal(
  services: MimirSegmentProviderServices,
  itemId: string,
  artifactSelectionOptions: PlayableArtifactSelectionOptions,
): Promise<SegmentProvider> {
  const { apiActions, logger } = services;

  const options = getCutterPlayableItemGetOptions();

  let closed = false;

  const onDurationChangedListeners : ((duration: Fraction) => void)[] = [];

  let segmentedMedia: Partial<Record<StreamRoleId, SegmentedMediaInfo>> = {};

  const item  = await apiActions.getPlayableItem(itemId, options);
  if (item === null) {
    throw Error('Failed to get playable item');
  }

  if (item.availableArtifacts.length === 0) {
    throw new ItemNotPlayableYet();
  }

  const artifactAndQuality = selectCutterPlayableArtifact(item, artifactSelectionOptions);

  if (artifactAndQuality === null) {
    throw new ItemNotPlayableYet();
  }

  const { artifact, quality } = artifactAndQuality;

  /*
    TAMS items carry no playableDuration: the flow itself is the source of
    truth, and a flow with no segments yet is still ingesting.
  */
  const tamsSource = artifact.type === 'tams' ? createTamsSegmentSource(services, itemId, artifact) : null;

  let mostRecentDuration: Fraction;
  if (tamsSource !== null) {
    mostRecentDuration = await tamsSource.getDuration();
    if (compareFractions(mostRecentDuration, FRAC_ZERO) <= 0) {
      throw new ItemNotPlayableYet();
    }
  } else if (item.playableDuration) {
    mostRecentDuration = restoreAccurateDuration(logger, item.playableDuration, item.frameDuration?.precise ?? null);
  } else {
    throw new ItemNotPlayableYet();
  }

  const addColorSpaceInfo = (encodedData: EncodedData) => {
    const colorSpace = item.colorSpace ?? null;
    if (colorSpace === null) {
      return encodedData;
    }

    return {
      ...encodedData,
      encodedVideoData: encodedData.encodedVideoData ? {
        ...encodedData.encodedVideoData,
        colorSpace,
      } : undefined,
    }
  };

  const segmentSource = tamsSource ??
    await createSegmentSource(
      services,
      item,
      artifact,
      (streamRoleId: StreamRoleId) => segmentedMedia[streamRoleId] ?? null,
    );

  const pollListenerId = services.itemPollingOps.addPollListener(itemId, ['playableDuration', 'segmentedMedia'], response => {
    if (response === null) {
      return;
    }

    segmentedMedia = response.segmentedMedia;

    if (response.playableDuration !== null) {
      const playableDuration = restoreAccurateDuration(logger, response.playableDuration, item.frameDuration?.precise ?? null);
      if (mostRecentDuration === null || compareFractions(mostRecentDuration, playableDuration) !== 0) {
        mostRecentDuration = playableDuration;
        onDurationChangedListeners.forEach((l) => l(playableDuration));
      }
    }
  });


  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      return segmentSource.getSegmentInfoAt(position);
    },
    async getSegment(id: string): Promise<EncodedData> {
      return addColorSpaceInfo(await segmentSource.getSegment(id));
    },
    async getDuration(): Promise<Fraction> {
      if (tamsSource !== null) {
        mostRecentDuration = await tamsSource.getDuration();
      }
      return mostRecentDuration;
    },
    async getVirtualClipInfo(): Promise<VirtualClipInfo | null> {
      return item.virtualClipInfo;
    },
    async getCurrentQuality(): Promise<PlaybackQuality> {
      return quality;
    },
    async getAvailableQualities(): Promise<QualityOption[]> {
      return getAvailableQualitiesFromItem(item, null);
    },
    close(): void {
      if (closed) {
        throw new Error('MimirSegmentProvider already closed');
      }
      closed = true;
      services.itemPollingOps.removePollListener(pollListenerId);
    },
    addDurationChangedListener(onDurationChanged: (duration: Fraction) => void): () => void {
      onDurationChangedListeners.push(onDurationChanged);
      return () => {
        const index = onDurationChangedListeners.indexOf(onDurationChanged);
        if (index !== -1) {
          onDurationChangedListeners.splice(index, 1);
        }
      };
    }
  };
}

interface SegmentSource {
  getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null>;
  getSegment(id: string): Promise<EncodedData>;
}

async function createSegmentSource(
  services: {
    apiActions: VideoApiActions,
    clock: Clock,
    demuxTs: (bytes: Uint8Array, firstSegment: boolean) => Promise<EncodedData>,
    fetch: BasicFetch,
    logger: Logger,
  },
  item: PlayableItemDto,
  playableArtifact: PlayableArtifact,
  getSegmentedMediaInfo: (streamRoleId: StreamRoleId) => SegmentedMediaInfo | null,
): Promise<SegmentSource> {
  switch (playableArtifact.type) {
    case 'flatEditProxy':
    case 'flatWebProxy':
    case 'flatHighRes': {
      const webProxyHasOriginalAudioLayout = item.proxyAudioTrackUrls.length === 2 && item.webProxyAudioDownMixInfo === null;

      const useAudioProxies = (playableArtifact.type === 'flatWebProxy') && !webProxyHasOriginalAudioLayout;

      const useMainProxy = !useAudioProxies || (item.itemType === 'video');

      return createFlatMp4SegmentSource(
        services,
        useMainProxy ? playableArtifact : null,
        useAudioProxies ? item.proxyAudioTrackUrls : null,
      );
    }
    case 'tams': {
      throw new Error('TAMS items are read through their own segment source');
    }
    case 'stream': {
      // Known limitation: We don't support stream sets that require multiple
      // segment streams. For instance fmp4 where video and audio is in separate
      // fragment streams.
      const streamId = playableArtifact.streamSet.at(0);
      if (streamId === undefined) {
        throw new Error('Zero streams in playable artifact of type "stream".');
      }
      if (playableArtifact.streamSet.length > 1) {
        throw new Error('Playable artifact with multiple streams is not supported.');
      }
      return await createStreamBasedSegmentSource(services, item, streamId, getSegmentedMediaInfo);
    }
  }
}

async function createStreamBasedSegmentSource(
  services: {
    apiActions: VideoApiActions,
    clock: Clock,
    demuxTs: (bytes: Uint8Array, firstSegment: boolean) => Promise<EncodedData>,
    fetch: BasicFetch,
    logger: Logger,
  },
  item: PlayableItemDto,
  streamId: StreamRoleId,
  getSegmentedMediaInfo: (streamRoleId: StreamRoleId) => SegmentedMediaInfo | null,
): Promise<SegmentSource> {
  switch (streamId) {
    case StreamRoleId.EditProxyFull: {
      return createFmp4SegmentSource(services, item, streamId, getSegmentedMediaInfo);
    }
    case StreamRoleId.LiveRecordHighresTs:
    case StreamRoleId.LiveRecordWebproxyTs:
    case StreamRoleId.LiveRecordAudioOnlyHighresTs:
    case StreamRoleId.LiveRecordAudioOnlyProxyTs: {
      return createTsSegmentSource(services, item, streamId, getSegmentedMediaInfo);
    }
    default:
      throw new Error(`Unsupported stream role id: ${streamId}`);
  }
}

async function createFlatMp4SegmentSource(
  services: {
    fetch: BasicFetch,
    logger: Logger,
  },
  playableArtifact: PlayableArtifactFlat | null,
  proxyAudioTrackUrls: string[] | null,
): Promise<SegmentSource> {
  const { fetch, logger } = services;
  let mp4Info : Mp4Info | undefined;
  if (playableArtifact !== null) {
    const ignoreAudio = (proxyAudioTrackUrls !== null);
    mp4Info = await fetchAndInspectMoov(fetch, playableArtifact.url, undefined, false, ignoreAudio);
  }

  if (proxyAudioTrackUrls !== null) {
    for (const proxyAudioTrackUrl of proxyAudioTrackUrls) {
      mp4Info = await fetchAndInspectMoov(fetch, proxyAudioTrackUrl, mp4Info, true, false);
    }
  }

  if (mp4Info === undefined) {
    throw Error('Item has no media');
  }

  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      const virtualSegmentIndex = findVirtualSegmentIndex(position, mp4Info.virtualSegments);
      if (virtualSegmentIndex === -1) {
        return null;
      }
      const virtualSegment = mp4Info.virtualSegments[virtualSegmentIndex];
      invariant(virtualSegment !== undefined, 'Expected virtualSegment');
      return {
        id: `${virtualSegmentIndex}`,
        start: virtualSegment.inclusiveStartInSeconds,
        end: virtualSegment.exclusiveEndInSeconds,
      };
    },
    async getSegment(id: string): Promise<EncodedData> {
      const segmentIndex = parseInt(id);
      if (segmentIndex.toString() !== id) {
        throw new Error(`Bad segment id: ${id}`);
      }

      const virtualSegment = mp4Info.virtualSegments[segmentIndex];
      invariant(virtualSegment !== undefined, 'Expected virtualSegment');
      const byteArrays = await fetchByteArrays(fetch, virtualSegment);
      return buildEncodedDataFromVirtualSegment(logger, byteArrays, virtualSegment, mp4Info);
    },
  };
}

async function createTsSegmentSource(
  services: {
    apiActions: VideoApiActions,
    clock: Clock,
    demuxTs: (bytes: Uint8Array, firstSegment: boolean) => Promise<EncodedData>,
    fetch: BasicFetch,
    logger: Logger,
  },
  item : PlayableItemDto,
  streamRoleId:
    StreamRoleId.LiveRecordHighresTs |
    StreamRoleId.LiveRecordWebproxyTs |
    StreamRoleId.LiveRecordAudioOnlyProxyTs |
    StreamRoleId.LiveRecordAudioOnlyHighresTs,
  getSegmentedMediaInfo: (streamRoleId: StreamRoleId) => SegmentedMediaInfo | null,
): Promise<SegmentSource> {
  const { apiActions, clock, demuxTs, fetch, logger } = services;

  if (item.media === null) {
    throw Error('Item has no media');
  }
  const media = item.media;

  const itemSegmentCache =
    new ItemSegmentUrlCache(
      apiActions,
      item.id,
      'get',
      streamRoleId,
      10,
    );

  const segmentFetch = resilientFetch(
    { fetch, AbortController },
    { shouldRetry: isRetryableSegmentResponse, retryDelayLimit: 30_000 },
    clock,
    services.logger,
  );

  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      const segmentDuration = media.segmentDuration;

      const segmentIndex =
          Math.floor(approximateFractionValue(divideFractions(position, segmentDuration)));

      const segmentCount = getSegmentedMediaInfo(streamRoleId)?.segmentCount ?? media.segmentCount;
      if (segmentIndex < 0 || segmentIndex >= segmentCount || isNaN(segmentIndex)) {
        return null;
      }

      const segmentIndexAsFraction = frac(segmentIndex, 1);

      return {
        id: segmentIndex.toString(),
        start: multiplyFractions(segmentIndexAsFraction, segmentDuration),
        end: addFractions(multiplyFractions(segmentIndexAsFraction, segmentDuration), segmentDuration),
      };
    },
    async getSegment(id: string): Promise<EncodedData> {
      const segmentIndex = parseInt(id);
      if (segmentIndex.toString() !== id) {
        throw new Error(`Bad segment id: ${id}`);
      }

      const url = await itemSegmentCache.getMediaSegmentUrl(segmentIndex);

      logger.info(`MimirSegmentProvider: fetching segment: ${segmentIndex}`);

      const response = await segmentFetch(url, { });

      if (!response.ok || response.body === null) {
        throw new Error(`Failed to fetch segment: ${response.status} ${response.statusText}`);
      }

      const bytes = await readWholeStream(response.body);
      return await demuxTs(bytes, false);
    },
  };
}

async function createFmp4SegmentSource(
  services: {
    apiActions: VideoApiActions,
    clock: Clock,
    fetch: BasicFetch,
    logger: Logger,
  },
  item : PlayableItemDto,
  streamRoleId: StreamRoleId.EditProxyFull,
  getSegmentedMediaInfo: (streamRoleId: StreamRoleId) => SegmentedMediaInfo | null,
): Promise<SegmentSource> {
  const { apiActions, clock, fetch, logger } = services;

  if (item.media === null) {
    throw Error('Item has no media');
  }
  const media = item.media;

  const segmentFetch = resilientFetch(
    { fetch, AbortController },
    { shouldRetry: isRetryableSegmentResponse, retryDelayLimit: 30_000 },
    clock,
    services.logger,
  );

  const itemSegmentCache =
    new ItemSegmentUrlCache(
      apiActions,
      item.id,
      'get',
      streamRoleId,
      10,
    );

  const url = await itemSegmentCache.getMediaSegmentUrl(0);
  logger.info('MimirSegmentProvider: fetching init fmp4 segment');
  const response = await fetch(url, { });

  if (!response.ok || (response.body === null)) {
    throw new Error(`Failed to fetch init segment: ${response.status} ${response.statusText}`);
  }
  const body = response.body;
  const fmp4InitSegmentBytes = await readWholeStream(body);
  const mp4Info = decodeFmp4InitSegment(fmp4InitSegmentBytes, false, false);

  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      const segmentDuration = media.segmentDuration;

      const segmentIndex =
          Math.floor(approximateFractionValue(divideFractions(position, segmentDuration)));

      const segmentCount = getSegmentedMediaInfo(streamRoleId)?.segmentCount ?? media.segmentCount;
      if (segmentIndex < 0 || segmentIndex >= segmentCount || isNaN(segmentIndex)) {
        return null;
      }

      const segmentIndexAsFraction = frac(segmentIndex, 1);

      return {
        // Segment zero is the init segment
        id: (segmentIndex + 1).toString(),
        start: multiplyFractions(segmentIndexAsFraction, segmentDuration),
        end: addFractions(multiplyFractions(segmentIndexAsFraction, segmentDuration), segmentDuration),
      };
    },
    async getSegment(id: string): Promise<EncodedData> {
      const segmentIndex = parseInt(id);
      if (segmentIndex.toString() !== id) {
        throw new Error(`Bad segment id: ${id}`);
      }
      const url = await itemSegmentCache.getMediaSegmentUrl(segmentIndex);

      logger.info(`MimirSegmentProvider: fetching segment: ${segmentIndex}`);

      const response = await segmentFetch(url, { });

      if (!response.ok || response.body === null) {
        throw new Error(`Failed to fetch segment: ${response.status} ${response.statusText}`);
      }

      const bytes = await readWholeStream(response.body);
      return demuxFmp4Segment(logger, mp4Info, bytes);
    },
  };
}

function createKey(
  itemId: string,
  options: PlayableArtifactSelectionOptions,
): string {
  return [
    itemId,
    options.specificQuality ?? '',
    options.autoSelectHint,
    options.blacklistedQualities.join(':'),
  ].join('#');
}

const ONE_MILLISECOND : Fraction = frac(1, 1000);

// Since the duration received from the backend is often derived from technical
// metadata where the duration is stored as whole milliseconds you can end up
// with scenarios where a duration of 200.333 ms gets rounded 200 ms and since
// we only consider frames entirely within the duration as whole frames we end
// up losing a frame. The solution is to just add 1 millisecond to the
// duration and floor to the nearest frame.
function restoreAccurateDuration(logger: Logger, duration: Fraction, frameDuration: Fraction | null): Fraction {
  if (frameDuration === null) {
    logger.warn(`Unable to restore accurate duration for item. (duration=${formatFractionAsExpression(duration)})`);
    return duration;
  }
  const frameCount = floorFraction(divideFractions(addFractions(duration, ONE_MILLISECOND), frameDuration));
  return multiplyFractionBy(frameDuration, frameCount);
}

